import { DEG, unionRects, type Point, type Rect } from './geometry';
import {
  CONNECTOR_CAPS,
  type BoxAnchor,
  type ConnectorCap,
  type ConnectorEnd,
  type ConnectorProps,
  type Geometry,
  type Item,
} from './schema';

/**
 * Connector geometry: where a connector's ends land on the items they are attached to, the path
 * each route takes between them, its bounds, where its label sits, and the edits that keep it consistent (loose ends
 * carried by a move, ends let go of their items, bend points re-based). Pure: the read index
 * resolves connectors against the document, the board against the live drag geometry.
 *
 * An attached end is either fixed (a side dot of the item: it turns and scales with it and the
 * path leaves at right angles to that side) or floating (it aims at the item's centre and stops
 * at its outline, so it slides round the item as things move). Bend points live in the frame
 * running from the start's reference point to the end's, so a curve keeps its shape, turning and
 * stretching with its ends.
 */

/** An item box an end can attach to. */
export interface AttachBox extends Geometry {
  /** The outline is an ellipse (ellipse shapes), not the box. */
  round: boolean;
  /** Frames only take ends near their outline: their inside belongs to what they hold. */
  frame: boolean;
}

export type BoxLookup = (id: string) => AttachBox | null;

/** The box an end attached to this item lands on; connectors and ink take no ends. */
export function attachBoxOf(item: Item | undefined): AttachBox | null {
  if (!item || item.type === 'connector' || item.type === 'stroke') return null;
  return {
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
    rotation: item.rotation || 0,
    round: item.type === 'shape' && item.props.kind === 'ellipse',
    frame: item.type === 'frame',
  };
}

/** The four side dots an end snaps to: top, right, bottom, left. */
export const SIDE_ANCHORS: readonly BoxAnchor[] = [
  { x: 0.5, y: 0 },
  { x: 1, y: 0.5 },
  { x: 0.5, y: 1 },
  { x: 0, y: 0.5 },
];

// ---- small vector helpers -------------------------------------------------------------------

const add = (a: Point, b: Point): Point => ({ x: a.x + b.x, y: a.y + b.y });
const sub = (a: Point, b: Point): Point => ({ x: a.x - b.x, y: a.y - b.y });
const scale = (a: Point, k: number): Point => ({ x: a.x * k, y: a.y * k });
const len = (a: Point): number => Math.hypot(a.x, a.y);
const dist = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerpPt = (a: Point, b: Point, t: number): Point => ({ x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) });
const ZERO: Point = { x: 0, y: 0 };
function unit(a: Point): Point {
  const l = len(a);
  return l < 1e-9 ? ZERO : { x: a.x / l, y: a.y / l };
}
function turn(v: Point, deg: number): Point {
  if (!deg) return v;
  const r = deg * DEG;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
}
const centreOf = (b: Geometry): Point => ({ x: b.x + b.w / 2, y: b.y + b.h / 2 });

/** A point of a box given in its own 0..1 units, in the world. */
export function boxPoint(b: Geometry, a: BoxAnchor): Point {
  return add(centreOf(b), turn({ x: (a.x - 0.5) * b.w, y: (a.y - 0.5) * b.h }, b.rotation));
}

/** A world point in a box's own 0..1 units (rotation undone). */
export function boxLocal(b: Geometry, p: Point): BoxAnchor {
  const l = turn(sub(p, centreOf(b)), -b.rotation);
  return { x: b.w ? l.x / b.w + 0.5 : 0.5, y: b.h ? l.y / b.h + 0.5 : 0.5 };
}

/** Is the world point inside the item's outline (an ellipse for round ones)? */
export function insideBox(b: AttachBox, p: Point): boolean {
  const a = boxLocal(b, p);
  if (b.round) return (a.x - 0.5) ** 2 + (a.y - 0.5) ** 2 <= 0.25;
  return a.x >= 0 && a.x <= 1 && a.y >= 0 && a.y <= 1;
}

/** Outward normal at an anchor: of the side it is nearest to (in world units), turned with the box. */
function anchorNormal(b: Geometry, a: BoxAnchor): Point {
  const gaps = [a.y * b.h, (1 - a.x) * b.w, (1 - a.y) * b.h, a.x * b.w];
  const side = gaps.indexOf(Math.min(...gaps));
  const n = [{ x: 0, y: -1 }, { x: 1, y: 0 }, { x: 0, y: 1 }, { x: -1, y: 0 }][side]!;
  return turn(n, b.rotation);
}

/** Where a ray from the box centre along `dir` leaves its outline. */
export function outlineExit(b: AttachBox, dir: Point): Point {
  const c = centreOf(b);
  const d = unit(dir);
  if (d === ZERO) return { x: c.x + b.w / 2, y: c.y };
  const l = turn(d, -b.rotation);
  const hw = Math.max(1e-6, b.w / 2);
  const hh = Math.max(1e-6, b.h / 2);
  const t = b.round
    ? 1 / Math.sqrt((l.x / hw) ** 2 + (l.y / hh) ** 2)
    : Math.min(Math.abs(l.x) > 1e-9 ? hw / Math.abs(l.x) : Infinity, Math.abs(l.y) > 1e-9 ? hh / Math.abs(l.y) : Infinity);
  return add(c, scale(d, t));
}

// ---- the bend frame -------------------------------------------------------------------------

/** The frame bend points are kept in: origin at the start's reference, (dx, dy) to the end's. */
export interface BendFrame {
  o: Point;
  dx: number;
  dy: number;
}

export function bendFrame(a: Point, b: Point): BendFrame {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  // both references on one spot: a unit frame, so bends are plain offsets until they part
  return dx * dx + dy * dy < 1e-6 ? { o: a, dx: 1, dy: 0 } : { o: a, dx, dy };
}

export function fromFrame(f: BendFrame, u: number, v: number): Point {
  return { x: f.o.x + u * f.dx - v * f.dy, y: f.o.y + u * f.dy + v * f.dx };
}

export function toFrame(f: BendFrame, p: Point): [number, number] {
  const qx = p.x - f.o.x;
  const qy = p.y - f.o.y;
  const l2 = f.dx * f.dx + f.dy * f.dy;
  return [(qx * f.dx + qy * f.dy) / l2, (qy * f.dx - qx * f.dy) / l2];
}

/** Bend points moved from one frame to another without changing where they are in the world. */
export function rebaseBends(bend: number[], from: BendFrame, to: BendFrame): number[] {
  const out: number[] = [];
  for (let i = 0; i + 1 < bend.length; i += 2) out.push(...toFrame(to, fromFrame(from, bend[i]!, bend[i + 1]!)));
  return out;
}

// ---- paths ----------------------------------------------------------------------------------

/** A run of the path: a straight line to `to`, or a cubic curve when it has control points. */
export interface PathSeg {
  c1?: Point;
  c2?: Point;
  to: Point;
}

export interface ConnectorPath {
  from: Point;
  segs: PathSeg[];
}

export interface ConnectorGeometry {
  path: ConnectorPath;
  start: Point;
  end: Point;
  /** The reference points the bend frame runs between. */
  refs: [Point, Point];
  frame: BendFrame;
  /** Bend points in the world (straight and curved routes), start to end. */
  bends: Point[];
  /**
   * Elbow route with a middle run: the stub ends the route turns from (a near the start, b near
   * the end), the axis the run slides along, and the handle point in its middle.
   */
  elbow: { a: Point; b: Point; axis: 'x' | 'y'; at: Point } | null;
  /** Bounds of the path and its control points, without width or heads. */
  hull: Rect;
}

/** How far an elbow route runs straight out of an item before it turns (further when a cap or head needs it). */
const ELBOW_STUB = 24;
/** Curve handles at a fixed side dot, as a part of the run they start: a firm exit. */
const ANCHOR_HANDLE = 0.5;
/** Curve handles elsewhere: a third of the run, as smooth splines have them. */
const SMOOTH_HANDLE = 1 / 3;

interface EndBase {
  box: AttachBox | null;
  /** The bend frame's reference: a fixed end's point, a floating end's item centre, a loose end itself. */
  ref: Point;
  /** Fixed ends: the outward normal of their side. */
  dir: Point | null;
  floating: boolean;
}

function endBase(end: ConnectorEnd, boxOf: BoxLookup): EndBase {
  const box = end.item ? boxOf(end.item) : null;
  if (!box) return { box: null, ref: { x: end.x, y: end.y }, dir: null, floating: false };
  if (end.anchor) return { box, ref: boxPoint(box, end.anchor), dir: anchorNormal(box, end.anchor), floating: false };
  return { box, ref: centreOf(box), dir: null, floating: true };
}

/** Resolves a connector: its ends on their items and the path its route takes. */
export function connectorGeometry(p: ConnectorProps, boxOf: BoxLookup): ConnectorGeometry {
  const s = endBase(p.start, boxOf);
  const e = endBase(p.end, boxOf);
  const frame = bendFrame(s.ref, e.ref);
  const refs: [Point, Point] = [s.ref, e.ref];
  if (p.route === 'elbow') return elbowGeometry(p, s, e, frame, refs);
  const bends: Point[] = [];
  for (let i = 0; i + 1 < p.bend.length; i += 2) bends.push(fromFrame(frame, p.bend[i]!, p.bend[i + 1]!));
  // a floating end aims at its neighbour on the path
  const start = s.floating ? outlineExit(s.box!, sub(bends[0] ?? e.ref, s.ref)) : s.ref;
  const end = e.floating ? outlineExit(e.box!, sub(bends[bends.length - 1] ?? s.ref, e.ref)) : e.ref;
  const pts = [start, ...bends, end];
  const segs = p.route === 'curved' ? curveSegs(pts, s.dir, e.dir) : pts.slice(1).map((to) => ({ to }));
  const path = { from: start, segs };
  return { path, start, end, refs, frame, bends, elbow: null, hull: hullOf(path) };
}

/**
 * A smooth curve through the points. Each inner point gets a tangent along the line joining its
 * neighbours; a fixed end leaves along its side's normal; any other end turns towards its
 * neighbour's handle, so the first and last runs bend naturally instead of starting straight.
 */
function curveSegs(pts: Point[], dirS: Point | null, dirE: Point | null): PathSeg[] {
  const n = pts.length;
  if (n === 2 && !dirS && !dirE) return [{ to: pts[1]! }];
  const t: (Point | null)[] = pts.map((_, i) => (i > 0 && i < n - 1 ? unit(sub(pts[i + 1]!, pts[i - 1]!)) : null));
  const k: number[] = pts.map(() => SMOOTH_HANDLE);
  if (dirS) {
    t[0] = dirS;
    k[0] = ANCHOR_HANDLE;
  }
  if (dirE) {
    t[n - 1] = scale(dirE, -1);
    k[n - 1] = ANCHOR_HANDLE;
  }
  if (!t[0]) {
    const run = dist(pts[0]!, pts[1]!);
    t[0] = unit(sub(sub(pts[1]!, scale(t[1]!, run * k[1]!)), pts[0]!));
  }
  if (!t[n - 1]) {
    const run = dist(pts[n - 2]!, pts[n - 1]!);
    t[n - 1] = unit(sub(pts[n - 1]!, add(pts[n - 2]!, scale(t[n - 2]!, run * k[n - 2]!))));
  }
  const segs: PathSeg[] = [];
  for (let i = 0; i + 1 < n; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const run = dist(a, b);
    segs.push({ c1: add(a, scale(t[i]!, run * k[i]!)), c2: sub(b, scale(t[i + 1]!, run * k[i + 1]!)), to: b });
  }
  return segs;
}

/** A unit axis direction from `from` towards `to`, along whichever axis is longer. */
function toward(from: Point, to: Point): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return Math.abs(dx) >= Math.abs(dy) ? { x: dx < 0 ? -1 : 1, y: 0 } : { x: 0, y: dy < 0 ? -1 : 1 };
}

/** A direction snapped to the nearest axis. */
function axisOf(d: Point): Point {
  return Math.abs(d.x) >= Math.abs(d.y) ? { x: d.x < 0 ? -1 : 1, y: 0 } : { x: 0, y: d.y < 0 ? -1 : 1 };
}

/**
 * Right angles: each end leaves its item along an axis (a fixed end along its side, any other
 * towards the other end) and runs a short stub; then the route turns once (the two axes cross)
 * or twice, with a middle run whose place `mid` sets.
 */
function elbowGeometry(p: ConnectorProps, s: EndBase, e: EndBase, frame: BendFrame, refs: [Point, Point]): ConnectorGeometry {
  const ds = s.dir ? axisOf(s.dir) : toward(s.ref, e.ref);
  const de = e.dir ? axisOf(e.dir) : toward(e.ref, s.ref);
  // a floating end leaves from the middle of the side facing its way
  const start = s.floating ? outlineExit(s.box!, ds) : s.ref;
  const end = e.floating ? outlineExit(e.box!, de) : e.ref;
  // a head or cap sits on a straight run, not round a corner
  const stub = Math.max(ELBOW_STUB, p.kind === 'block' ? p.width * BLOCK_HEAD_LEN + 8 : capSize(p.width) + 4);
  const a = add(start, scale(ds, s.box ? stub : 0));
  const b = add(end, scale(de, e.box ? stub : 0));
  const mid = Number.isFinite(p.mid) ? p.mid : 0.5;
  let pts: Point[];
  let elbow: ConnectorGeometry['elbow'] = null;
  const hs = ds.x !== 0;
  const he = de.x !== 0;
  if (hs && he) {
    const mx = lerp(a.x, b.x, mid);
    pts = [start, a, { x: mx, y: a.y }, { x: mx, y: b.y }, b, end];
    elbow = { a, b, axis: 'x', at: { x: mx, y: (a.y + b.y) / 2 } };
  } else if (!hs && !he) {
    const my = lerp(a.y, b.y, mid);
    pts = [start, a, { x: a.x, y: my }, { x: b.x, y: my }, b, end];
    elbow = { a, b, axis: 'y', at: { x: (a.x + b.x) / 2, y: my } };
  } else if (hs) {
    pts = [start, a, { x: b.x, y: a.y }, b, end];
  } else {
    pts = [start, a, { x: a.x, y: b.y }, b, end];
  }
  pts = simplifyRun(pts);
  const path = { from: pts[0]!, segs: pts.slice(1).map((to) => ({ to })) };
  return { path, start, end, refs, frame, bends: [], elbow, hull: hullOf(path) };
}

/** Drops repeated points and points in the middle of a straight run. */
function simplifyRun(pts: Point[]): Point[] {
  const out: Point[] = [];
  for (const q of pts) {
    const last = out[out.length - 1];
    if (last && dist(last, q) < 0.01) continue;
    const prev = out[out.length - 2];
    if (prev && last) {
      const between = (v: number, lo: number, hi: number) => v >= Math.min(lo, hi) - 0.01 && v <= Math.max(lo, hi) + 0.01;
      const sameX = Math.abs(prev.x - last.x) < 0.01 && Math.abs(last.x - q.x) < 0.01 && between(last.y, prev.y, q.y);
      const sameY = Math.abs(prev.y - last.y) < 0.01 && Math.abs(last.y - q.y) < 0.01 && between(last.x, prev.x, q.x);
      if (sameX || sameY) out.pop();
    }
    out.push(q);
  }
  if (out.length === 1) out.push(out[0]!);
  return out;
}

function hullOf(path: ConnectorPath): Rect {
  let x0 = path.from.x;
  let y0 = path.from.y;
  let x1 = x0;
  let y1 = y0;
  const take = (q: Point | undefined) => {
    if (!q) return;
    x0 = Math.min(x0, q.x);
    y0 = Math.min(y0, q.y);
    x1 = Math.max(x1, q.x);
    y1 = Math.max(y1, q.y);
  };
  for (const s of path.segs) {
    take(s.c1);
    take(s.c2);
    take(s.to);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

// ---- sizes of what is drawn at the ends -------------------------------------------------------

/** Length of a line's end cap. */
export const capSize = (width: number): number => 6 + width * 3;
/** Block arrow heads, as multiples of the body width. */
export const BLOCK_HEAD_LEN = 1.25;
export const BLOCK_HEAD_HALF = 1.1;
/** A block arrow's outline width, 0 without one. */
export const blockBorder = (p: ConnectorProps): number => (p.stroke === 'none' ? 0 : Math.min(4, Math.max(1.5, p.width * 0.07)));

/**
 * The connector's box: its path's bounds grown by what is drawn around it (width, caps, heads),
 * and its label's box (with some slack, as the label's width is only estimated here).
 */
export function connectorBounds(p: ConnectorProps, geo: ConnectorGeometry): Rect {
  let pad: number;
  if (p.kind === 'block') {
    const heads = p.startCap !== 'none' || p.endCap !== 'none';
    pad = p.width * (heads ? BLOCK_HEAD_HALF : 0.75) + blockBorder(p) + 1;
  } else {
    const caps = p.startCap !== 'none' || p.endCap !== 'none';
    pad = Math.max(p.width / 2, caps ? capSize(p.width) * 0.6 : 0) + 1;
  }
  const h = geo.hull;
  const box = { x: h.x - pad, y: h.y - pad, w: h.w + pad * 2, h: h.h + pad * 2 };
  const label = labelRect(p, geo);
  if (!label) return box;
  const slack = p.labelSize * 0.5;
  return unionRects([box, { x: label.x - slack, y: label.y - slack, w: label.w + slack * 2, h: label.h + slack * 2 }])!;
}

// ---- the label --------------------------------------------------------------------------------

export const DEFAULT_LABEL_SIZE = 16;
/** The box round a label's text, as multiples of its font size (the board's .connector-label matches). */
export const LABEL_PAD_X = 0.4;
export const LABEL_PAD_Y = 0.15;
export const LABEL_LINE = 1.25;

/** Columns a line takes in a monospaced font: wide characters (CJK, emoji) take two. */
function columns(text: string): number {
  let n = 0;
  for (const ch of text) {
    const c = ch.codePointAt(0)!;
    const wide =
      (c >= 0x1100 && c <= 0x115f) ||
      (c >= 0x2e80 && c <= 0xa4cf) ||
      (c >= 0xac00 && c <= 0xd7a3) ||
      (c >= 0xf900 && c <= 0xfaff) ||
      (c >= 0xfe30 && c <= 0xfe4f) ||
      (c >= 0xff00 && c <= 0xff60) ||
      (c >= 0xffe0 && c <= 0xffe6) ||
      c >= 0x1f300;
    n += wide ? 2 : 1;
  }
  return n;
}

/** Width of a label's text: the board font is monospaced, 0.6 em a column. */
export const labelTextWidth = (text: string, size: number): number => columns(text) * size * 0.6;

/** Where the label sits: its centre, on the path. */
export const labelPoint = (p: ConnectorProps, geo: ConnectorGeometry): Point => pointAlong(geo.path, p.labelAt);

/** The box the label takes round its centre (text width estimated), or null without a label. */
export function labelRect(p: ConnectorProps, geo: ConnectorGeometry): Rect | null {
  if (!p.label) return null;
  const c = labelPoint(p, geo);
  const w = labelTextWidth(p.label, p.labelSize) + p.labelSize * LABEL_PAD_X * 2;
  const h = p.labelSize * (LABEL_LINE + LABEL_PAD_Y * 2);
  return { x: c.x - w / 2, y: c.y - h / 2, w, h };
}

/** The label's colour: its own, else the line's; on a block arrow, dark or light to read on the fill. */
export function labelInk(p: ConnectorProps): string {
  if (p.labelColor) return p.labelColor;
  if (p.kind === 'block') return isDark(p.fill) ? '#ffffff' : '#111111';
  return p.stroke && p.stroke !== 'none' ? p.stroke : '#111111';
}

function isDark(color: string): boolean {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return false;
  const hex = m[1]!.length === 3 ? m[1]!.replace(/./g, '$&$&') : m[1]!;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! < 0.5;
}

// ---- walking the path -----------------------------------------------------------------------

const CURVE_STEPS = 24;

function cubicAt(a: Point, c1: Point, c2: Point, b: Point, t: number): Point {
  const u = 1 - t;
  const w0 = u * u * u;
  const w1 = 3 * u * u * t;
  const w2 = 3 * u * t * t;
  const w3 = t * t * t;
  return { x: w0 * a.x + w1 * c1.x + w2 * c2.x + w3 * b.x, y: w0 * a.y + w1 * c1.y + w2 * c2.y + w3 * b.y };
}

/** The path as a polyline (curves sampled), world points. */
export function flattenPath(path: ConnectorPath, steps = CURVE_STEPS): Point[] {
  const out: Point[] = [path.from];
  let a = path.from;
  for (const s of path.segs) {
    if (s.c1 && s.c2) for (let i = 1; i <= steps; i++) out.push(cubicAt(a, s.c1, s.c2, s.to, i / steps));
    else out.push(s.to);
    a = s.to;
  }
  return out;
}

/** The point at parameter t of each run of the path (halfway: where a bend can be added), world points. */
export function runPoints(path: ConnectorPath, t = 0.5): Point[] {
  const out: Point[] = [];
  let a = path.from;
  for (const s of path.segs) {
    out.push(s.c1 && s.c2 ? cubicAt(a, s.c1, s.c2, s.to, t) : lerpPt(a, s.to, t));
    a = s.to;
  }
  return out;
}

/** The point a fraction of the way along the path by length (0 the start, 1 the end). */
export function pointAlong(path: ConnectorPath, t: number): Point {
  const pts = flattenPath(path);
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += dist(pts[i - 1]!, pts[i]!);
  let left = Math.max(0, Math.min(1, t)) * total;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const l = dist(a, b);
    if (l >= left && l > 1e-9) return lerpPt(a, b, left / l);
    left -= l;
  }
  return pts[pts.length - 1]!;
}

/** The fraction of the path's length (0..1) at the point of the path nearest to `p`. */
export function fractionNear(path: ConnectorPath, p: Point): number {
  const pts = flattenPath(path);
  let at = 0;
  let best = Infinity;
  let bestAt = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const d = sub(b, a);
    const l = len(d);
    const k = l > 1e-9 ? Math.max(0, Math.min(1, ((p.x - a.x) * d.x + (p.y - a.y) * d.y) / (l * l))) : 0;
    const gap = dist(p, lerpPt(a, b, k));
    if (gap < best) {
      best = gap;
      bestAt = at + k * l;
    }
    at += l;
  }
  return at > 1e-9 ? bestAt / at : 0.5;
}

/** Cumulative lengths along a segment's samples (index i is at t = i / steps). */
function segLengths(a: Point, s: PathSeg): number[] {
  if (!s.c1 || !s.c2) return [0, dist(a, s.to)];
  const out = [0];
  let prev = a;
  for (let i = 1; i <= CURVE_STEPS; i++) {
    const q = cubicAt(a, s.c1, s.c2, s.to, i / CURVE_STEPS);
    out.push(out[i - 1]! + dist(prev, q));
    prev = q;
  }
  return out;
}

/** The curve parameter at a length along a segment, from its sampled lengths. */
function tAt(cum: number[], d: number): number {
  const n = cum.length - 1;
  if (d <= 0) return 0;
  if (d >= cum[n]!) return 1;
  let i = 1;
  while (cum[i]! < d) i++;
  const f = (d - cum[i - 1]!) / Math.max(1e-9, cum[i]! - cum[i - 1]!);
  return (i - 1 + f) / n;
}

/** Splits a cubic at t (de Casteljau); returns the two halves' control points. */
function splitCubic(a: Point, c1: Point, c2: Point, b: Point, t: number): [Point, Point, Point, Point, Point, Point, Point] {
  const p01 = lerpPt(a, c1, t);
  const p12 = lerpPt(c1, c2, t);
  const p23 = lerpPt(c2, b, t);
  const p012 = lerpPt(p01, p12, t);
  const p123 = lerpPt(p12, p23, t);
  const m = lerpPt(p012, p123, t);
  return [a, p01, p012, m, p123, p23, b];
}

/** The piece of one segment between parameters t0 and t1. */
function subSeg(a: Point, s: PathSeg, t0: number, t1: number): { from: Point; seg: PathSeg } {
  if (!s.c1 || !s.c2) return { from: lerpPt(a, s.to, t0), seg: { to: lerpPt(a, s.to, t1) } };
  // cut at t1, then cut the left half at t0 / t1
  const [, l1, l2, m] = splitCubic(a, s.c1, s.c2, s.to, t1);
  const t = t1 > 1e-9 ? t0 / t1 : 0;
  const [, , , m2, r1, r2, r3] = splitCubic(a, l1, l2, m, t);
  return { from: m2, seg: { c1: r1, c2: r2, to: r3 } };
}

export function pathLength(path: ConnectorPath): number {
  let total = 0;
  let a = path.from;
  for (const s of path.segs) {
    const cum = segLengths(a, s);
    total += cum[cum.length - 1]!;
    a = s.to;
  }
  return total;
}

/**
 * The path without its first `cutStart` and last `cutEnd` units of length. When the path is too
 * short for both, the cuts shrink in proportion and a sliver is left in the middle.
 */
export function trimPath(path: ConnectorPath, cutStart: number, cutEnd: number): ConnectorPath {
  if (cutStart <= 0 && cutEnd <= 0) return path;
  const runs: { a: Point; s: PathSeg; cum: number[] }[] = [];
  let a = path.from;
  let total = 0;
  for (const s of path.segs) {
    const cum = segLengths(a, s);
    runs.push({ a, s, cum });
    total += cum[cum.length - 1]!;
    a = s.to;
  }
  let d0 = Math.max(0, cutStart);
  let d1 = total - Math.max(0, cutEnd);
  if (d1 - d0 < total * 0.02) {
    const k = (total * 0.98) / Math.max(1e-9, Math.max(0, cutStart) + Math.max(0, cutEnd));
    d0 = Math.max(0, cutStart) * k;
    d1 = total - Math.max(0, cutEnd) * k;
  }
  let from: Point | null = null;
  const segs: PathSeg[] = [];
  let at = 0;
  for (const r of runs) {
    const l = r.cum[r.cum.length - 1]!;
    const lo = at;
    const hi = at + l;
    at = hi;
    if (hi < d0 || lo > d1 || l < 1e-9) continue;
    const piece = subSeg(r.a, r.s, tAt(r.cum, d0 - lo), tAt(r.cum, d1 - lo));
    if (!from) from = piece.from;
    segs.push(piece.seg);
  }
  return from ? { from, segs } : { from: path.from, segs: [{ to: path.from }] };
}

/**
 * The tip of the path's start or end and the unit direction it points there, measured over the
 * last `reach` units of length (so a head on a curve sits along the curve's end).
 */
export function pathTip(path: ConnectorPath, which: 'start' | 'end', reach: number): { tip: Point; dir: Point } {
  const pts = flattenPath(path);
  if (which === 'start') pts.reverse();
  const tip = pts[pts.length - 1]!;
  let left = reach;
  for (let i = pts.length - 1; i > 0; i--) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const l = dist(a, b);
    if (l >= left) {
      const back = lerpPt(b, a, left / Math.max(1e-9, l));
      return { tip, dir: unit(sub(tip, back)) };
    }
    left -= l;
  }
  const dir = unit(sub(tip, pts[0]!));
  return { tip, dir: dir === ZERO ? { x: 1, y: 0 } : dir };
}

/** SVG path data, world coordinates. */
export function pathD(path: ConnectorPath): string {
  const f = (q: Point) => `${q.x.toFixed(2)},${q.y.toFixed(2)}`;
  let d = `M${f(path.from)}`;
  for (const s of path.segs) d += s.c1 && s.c2 ? `C${f(s.c1)} ${f(s.c2)} ${f(s.to)}` : `L${f(s.to)}`;
  return d;
}

/** Does the drawn path cross or lie in the rect? (Marquee selection.) */
export function pathHitsRect(path: ConnectorPath, r: Rect): boolean {
  const pts = flattenPath(path, 12);
  const inside = (q: Point) => q.x >= r.x && q.x <= r.x + r.w && q.y >= r.y && q.y <= r.y + r.h;
  if (pts.some(inside)) return true;
  const corners: Point[] = [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h },
    { x: r.x, y: r.y + r.h },
  ];
  for (let i = 1; i < pts.length; i++) {
    for (let j = 0; j < 4; j++) if (segmentsCross(pts[i - 1]!, pts[i]!, corners[j]!, corners[(j + 1) % 4]!)) return true;
  }
  return false;
}

function segmentsCross(a: Point, b: Point, c: Point, d: Point): boolean {
  const o = (p: Point, q: Point, r: Point) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const o1 = o(a, b, c);
  const o2 = o(a, b, d);
  const o3 = o(c, d, a);
  const o4 = o(c, d, b);
  return o1 * o2 < 0 && o3 * o4 < 0;
}

// ---- edits ----------------------------------------------------------------------------------

/** An end attached to an item that can still be found. */
export const isAttached = (end: ConnectorEnd, boxOf: BoxLookup): boolean => !!end.item && !!boxOf(end.item);

/**
 * The connector after its box went from `from` to `to` (a move, or a group scale): loose ends
 * are carried along; attached ends stay with their items. Bends follow the ends by themselves.
 */
export function mapLooseEnds(p: ConnectorProps, from: Rect, to: Rect, boxOf: BoxLookup): ConnectorProps {
  const sx = from.w > 1e-6 ? to.w / from.w : 1;
  const sy = from.h > 1e-6 ? to.h / from.h : 1;
  const map = (end: ConnectorEnd): ConnectorEnd =>
    isAttached(end, boxOf) ? end : { ...end, x: to.x + (end.x - from.x) * sx, y: to.y + (end.y - from.y) * sy };
  return { ...p, start: map(p.start), end: map(p.end) };
}

/**
 * The connector with some ends let go of their items, left exactly where they are; the bends are
 * re-based so the path keeps its shape.
 */
export function detachEnds(p: ConnectorProps, geo: ConnectorGeometry, which: { start?: boolean; end?: boolean }): ConnectorProps {
  if (!which.start && !which.end) return p;
  const loose = (q: Point): ConnectorEnd => ({ item: null, anchor: null, x: q.x, y: q.y });
  const start = which.start ? loose(geo.start) : p.start;
  const end = which.end ? loose(geo.end) : p.end;
  const frame = bendFrame(which.start ? geo.start : geo.refs[0], which.end ? geo.end : geo.refs[1]);
  return { ...p, start, end, bend: rebaseBends(p.bend, geo.frame, frame) };
}

/**
 * A copy's props: ends attached to items copied along follow the copies (`newIdOf`), any other
 * attached end lets go where it is (snapshots already did that, see BoardDoc.snapshotItems), and
 * loose points shift with the copy.
 */
export function copyConnectorProps(p: ConnectorProps, newIdOf: (id: string) => string | undefined, dx: number, dy: number): ConnectorProps {
  const copy = (end: ConnectorEnd): ConnectorEnd => {
    const id = end.item ? newIdOf(end.item) : undefined;
    return id ? { ...end, item: id, x: end.x + dx, y: end.y + dy } : { item: null, anchor: null, x: end.x + dx, y: end.y + dy };
  };
  return { ...p, start: copy(p.start), end: copy(p.end) };
}

// ---- reading stored props -------------------------------------------------------------------

const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const str = (v: unknown, fallback: string): string => (typeof v === 'string' && v ? v : fallback);
const oneOf = <T extends string>(v: unknown, options: readonly T[], fallback: T): T => (options.includes(v as T) ? (v as T) : fallback);

function readEnd(v: unknown): ConnectorEnd {
  const e = (v && typeof v === 'object' ? v : {}) as Partial<ConnectorEnd>;
  const a = e.anchor && typeof e.anchor === 'object' ? e.anchor : null;
  return {
    item: typeof e.item === 'string' && e.item ? e.item : null,
    anchor: a ? { x: num(a.x, 0.5), y: num(a.y, 0.5) } : null,
    x: num(e.x, 0),
    y: num(e.y, 0),
  };
}

/** Stored props with anything missing or malformed (a newer or older client's) set to defaults. */
export function readConnectorProps(raw: unknown): ConnectorProps {
  const p = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const bend = Array.isArray(p.bend) ? (p.bend as unknown[]).filter((v): v is number => typeof v === 'number' && Number.isFinite(v)) : [];
  return {
    kind: oneOf(p.kind, ['line', 'block'] as const, 'line'),
    route: oneOf(p.route, ['straight', 'elbow', 'curved'] as const, 'straight'),
    start: readEnd(p.start),
    end: readEnd(p.end),
    bend: bend.length % 2 ? bend.slice(0, -1) : bend,
    mid: num(p.mid, 0.5),
    startCap: oneOf<ConnectorCap>(p.startCap, CONNECTOR_CAPS, 'none'),
    endCap: oneOf<ConnectorCap>(p.endCap, CONNECTOR_CAPS, 'arrow'),
    stroke: str(p.stroke, '#111111'),
    width: Math.max(0.5, num(p.width, 3)),
    dash: oneOf(p.dash, ['solid', 'dashed', 'dotted'] as const, 'solid'),
    fill: str(p.fill, '#ffffff'),
    label: typeof p.label === 'string' ? p.label : '',
    labelAt: Math.max(0, Math.min(1, num(p.labelAt, 0.5))),
    labelSize: Math.max(4, num(p.labelSize, DEFAULT_LABEL_SIZE)),
    labelColor: typeof p.labelColor === 'string' && p.labelColor ? p.labelColor : null,
  };
}
